import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2,
  XCircle,
  ArrowLeft,
  ArrowRight,
  Trophy,
  RotateCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { lt } from "../localize";
import type { Module, ExamQuestion } from "../types";

interface Props {
  module: Module;
  onComplete: (score: number, passed: boolean) => void;
  onCancel: () => void;
}

export const ModuleExam = ({ module, onComplete, onCancel }: Props) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const exam = module.exam!;
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(
    new Array(exam.questions.length).fill(null),
  );
  const [submitted, setSubmitted] = useState(false);

  const question = exam.questions[currentQ];
  const selected = answers[currentQ];
  const isLast = currentQ === exam.questions.length - 1;
  const allAnswered = answers.every((a) => a !== null);

  const handleSelect = (idx: number) => {
    if (submitted) return;
    const next = [...answers];
    next[currentQ] = idx;
    setAnswers(next);
  };

  const handleSubmit = () => {
    const correct = exam.questions.reduce(
      (sum, q, i) =>
        sum + (q.options[answers[i]!]?.isCorrect ? 1 : 0),
      0,
    );
    const score = correct / exam.questions.length;
    const passed = score >= exam.passingScore;
    setSubmitted(true);
    // Delay to show results before calling onComplete
    setTimeout(() => onComplete(score, passed), 2500);
  };

  if (submitted) {
    const correct = exam.questions.reduce(
      (sum, q, i) =>
        sum + (q.options[answers[i]!]?.isCorrect ? 1 : 0),
      0,
    );
    const score = correct / exam.questions.length;
    const passed = score >= exam.passingScore;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center space-y-4">
          {passed ? (
            <>
              <Trophy className="h-16 w-16 text-secondary mx-auto" />
              <h2 className="text-2xl font-bold text-secondary">
                {t("learn_exam_passed")}
              </h2>
              <p className="text-muted-foreground">
                {t("learn_correct_answers", {
                  correct,
                  total: exam.questions.length,
                  percent: Math.round(score * 100),
                })}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("learn_all_lessons_unlocked")}
              </p>
            </>
          ) : (
            <>
              <XCircle className="h-16 w-16 text-destructive mx-auto" />
              <h2 className="text-2xl font-bold text-destructive">
                {t("learn_not_passed")}
              </h2>
              <p className="text-muted-foreground">
                {t("learn_correct_answers", {
                  correct,
                  total: exam.questions.length,
                  percent: Math.round(score * 100),
                })}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("learn_need_to_pass", {
                  percent: Math.round(exam.passingScore * 100),
                })}
              </p>
              <Button variant="outline" onClick={onCancel}>
                <RotateCcw className="mr-2 h-4 w-4" />
                {t("learn_go_back")}
              </Button>
            </>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="p-4 space-y-4 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">
              {t("learn_exam_title", { title: lt(module.title, lang) })}
            </h1>
            <p className="text-xs text-muted-foreground">
              {t("learn_question_of", {
                current: currentQ + 1,
                total: exam.questions.length,
                percent: Math.round(exam.passingScore * 100),
              })}
            </p>
          </div>
        </div>

        <Progress
          value={((currentQ + 1) / exam.questions.length) * 100}
          className="h-1.5"
        />

        {/* Question */}
        <Card className="p-5">
          <h2 className="text-lg font-bold mb-4">{lt(question.question, lang)}</h2>
          <div className="space-y-2">
            {question.options.map((opt, idx) => (
              <button
                key={idx}
                className={cn(
                  "w-full text-left p-4 rounded-lg border transition-all",
                  selected === idx
                    ? "border-secondary bg-secondary/10 ring-1 ring-secondary/30"
                    : "border-muted hover:border-muted-foreground/30",
                )}
                onClick={() => handleSelect(idx)}
              >
                <span className="text-sm">{lt(opt.text, lang)}</span>
              </button>
            ))}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setCurrentQ((q) => q - 1)}
            disabled={currentQ === 0}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("learn_previous")}
          </Button>
          {isLast ? (
            <Button
              className="flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/90"
              onClick={handleSubmit}
              disabled={!allAnswered}
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              {t("learn_submit_exam")}
            </Button>
          ) : (
            <Button
              className="flex-1"
              onClick={() => setCurrentQ((q) => q + 1)}
              disabled={selected === null}
            >
              {t("learn_next_question")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
